import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  listPromptTemplates,
  archivePromptTemplate,
  setDefaultTemplate,
} from '../services/api';
import Header from '../components/Header';
import PromptTemplateEditor from '../components/PromptTemplateEditor';
import TemplateUsageDialog from '../components/TemplateUsageDialog';
import TemplateVersionHistory from '../components/TemplateVersionHistory';

interface PromptTemplate {
  template_id: string;
  template_name: string;
  template_type: 'WP' | 'CR' | 'SYSTEM';
  content: string;
  description: string;
  variables: string[];
  is_default: boolean;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
  status: 'active' | 'archived';
}

type TemplateType = 'WP' | 'CR' | 'SYSTEM';

const PromptManagement: React.FC = () => {
  // WP Prompts is disabled for now - default to the CR Prompts tab.
  const [currentTab, setCurrentTab] = useState<number>(1);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [usageDialogOpen, setUsageDialogOpen] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState<boolean>(false);
  const [historyTemplateId, setHistoryTemplateId] = useState<string | null>(null);

  const templateTypes: TemplateType[] = ['WP', 'CR', 'SYSTEM'];
  const currentTemplateType = templateTypes[currentTab];

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  useEffect(() => {
    filterTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, searchQuery]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPromptTemplates(currentTemplateType);
      setTemplates(data.templates || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = templates.filter(
      (t) =>
        t.template_name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.variables.some((v) => v.toLowerCase().includes(query))
    );
    setFilteredTemplates(filtered);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchQuery('');
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const handleEditTemplate = (template: PromptTemplate) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  };

  const handleEditorClose = (refresh: boolean) => {
    setEditorOpen(false);
    setEditingTemplate(null);
    if (refresh) {
      loadTemplates();
    }
  };

  const handleArchiveTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to archive this template?')) {
      return;
    }

    try {
      await archivePromptTemplate(templateId);
      loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to archive template');
    }
  };

  const handleSetDefault = async (templateId: string, templateType: TemplateType) => {
    try {
      await setDefaultTemplate(templateId, templateType);
      loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to set default template');
    }
  };

  const handleViewUsage = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setUsageDialogOpen(true);
  };

  const handleUsageDialogClose = () => {
    setUsageDialogOpen(false);
    setSelectedTemplateId(null);
  };

  const handleViewHistory = (templateId: string) => {
    setHistoryTemplateId(templateId);
    setHistoryDialogOpen(true);
  };

  const handleHistoryDialogClose = (reverted: boolean) => {
    setHistoryDialogOpen(false);
    setHistoryTemplateId(null);
    if (reverted) {
      loadTemplates();
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Prompt Template Management
          </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
        >
          Create New Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="WP Prompts" disabled />
          <Tab label="CR Prompts" />
          <Tab label="System Prompts" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search templates by name, description, or variables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredTemplates.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {searchQuery ? 'No templates match your search.' : 'No templates created yet.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Template Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Variables</TableCell>
                    <TableCell>Attribute Mapping</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.template_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {template.template_name}
                          {template.is_default && (
                            <Chip label="Default" color="primary" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{template.description || '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {template.variables.map((v) => (
                            <Chip key={v} label={v} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(template as any).attribute_mappings && (template as any).attribute_mappings.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(template as any).attribute_mappings.map((m: any) => (
                              <Chip
                                key={m.mapping_id}
                                label={`${m.attribute_name} = ${m.attribute_value}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            None
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(template.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          label={template.status}
                          color={template.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditTemplate(template)}
                              disabled={template.status === 'archived'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!template.is_default && (
                            <Tooltip title="Set as Default">
                              <IconButton
                                size="small"
                                onClick={() => handleSetDefault(template.template_id, template.template_type)}
                                disabled={template.status === 'archived'}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="View Usage">
                            <IconButton
                              size="small"
                              onClick={() => handleViewUsage(template.template_id)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Version History">
                            <IconButton
                              size="small"
                              onClick={() => handleViewHistory(template.template_id)}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              onClick={() => handleArchiveTemplate(template.template_id)}
                              disabled={template.status === 'archived' || template.is_default}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      <PromptTemplateEditor
        open={editorOpen}
        template={editingTemplate}
        templateType={currentTemplateType}
        onClose={handleEditorClose}
      />

      {selectedTemplateId && (
        <TemplateUsageDialog
          open={usageDialogOpen}
          templateId={selectedTemplateId}
          onClose={handleUsageDialogClose}
        />
      )}

      {historyTemplateId && (
        <TemplateVersionHistory
          open={historyDialogOpen}
          templateId={historyTemplateId}
          onClose={handleHistoryDialogClose}
        />
      )}
      </Container>
    </>
  );
};

export default PromptManagement;
